import React from "react";
import helperStyles from "@src/components/helpers.module.scss";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { MyTooltip } from "@src/components/tooltip/Tooltip";
import { TicketIcon } from "@src/assets/general-icons/TicketIcon";
import { ShareIcon } from "@src/assets/general-icons/ShareIcon";
import { BarcodeIcon } from "@src/assets/general-icons/BarcodeIcon";
import { PDFIcon } from "@src/assets/general-icons/PDFIcon";
import { MobileIcon } from "@src/assets/filter-icons";
import { Zap, AlarmClock } from "lucide-react";
import DoubleCheckIcon from "@src/assets/general-icons/DoubleCheckIcon";
import { useAppSelector } from "@src/store/hooks";

export interface TicketPropertiesData {
	id: string | number;
	eDelivery?: boolean | null;
	ticketAttached?: boolean | number | null;
	sharedWithGT?: boolean | null;
	pdfsAttached?: number | null;
	barcodesAttached?: number | null;
	scheduledPricing?: boolean | null;
	flashSale?: boolean | null;
	onHold?: boolean | null;
	onHoldComments?: string | null;
	reviewed?: boolean | null;
}

export const TicketProperties: React.FC<{ data: TicketPropertiesData }> = ({ data }) => {
	const id = `properties${data.id}`;

	const mappedData = [
		{ id: `${id}sh_e_delivery`, popup: "E-Ticket", element: <MobileIcon />, shouldShow: data.eDelivery },
		{ id: `${id}ticket`, popup: "Ticket", element: <TicketIcon />, shouldShow: data.ticketAttached },
		{
			id: `${id}share`,
			popup: "Shared to one or more exchanges.",
			element: <ShareIcon />,
			shouldShow: data.sharedWithGT,
		},
		{
			id: `${id}pdf`,
			popup: `PDFs Attached: ${data.pdfsAttached}`,
			element: <PDFIcon />,
			shouldShow: data.pdfsAttached,
		},
		{
			id: `${id}barcode`,
			popup: `Barcodes: ${data.barcodesAttached}`,
			element: <BarcodeIcon />,
			shouldShow: data.barcodesAttached,
		},
		{
			id: `${id}scheduledPricing`,
			popup: "This listing is being updated by Scheduled Pricing",
			element: <div style={{ color: "#12b76a" }}>$</div>,
			shouldShow: data.scheduledPricing,
		},
		{
			id: `${id}flash_sale`,
			popup: "Flash Sale Enabled",
			element: (
				<div style={{ color: "#12b76a", display: "flex", alignItems: "center" }}>
					<Zap size={15} />
				</div>
			),
			shouldShow: data.flashSale,
		},
		{
			id: `${id}reviewed`,
			popup: "Ticket marked as reviewed",
			element: <DoubleCheckIcon />,
			shouldShow: data.reviewed,
		},
	];

	const hasHoldComments = !!data.onHoldComments && data.onHoldComments.trim().length > 0;
	const holdIcon = (
		<span style={{ display: "flex", alignItems: "center" }}>
			<AlarmClock size={15} />
		</span>
	);

	return (
		<div className={helperStyles.flexSmallGap}>
			{mappedData.map((x) => {
				if (!x.shouldShow) return;
				return <MyTooltip key={x.id} id={x.id} popup={x.popup} element={<span>{x.element}</span>} />;
			})}
			{data.onHold ? (
				hasHoldComments ? (
					<MyTooltip id={`${id}on_hold`} popup={`On Hold: ${data.onHoldComments}`} element={holdIcon} />
				) : (
					holdIcon
				)
			) : null}
		</div>
	);
};

const Properties: React.FC<{ rowData: EventInventoryRecord }> = ({ rowData }) => {
	const reviewedListingIds = useAppSelector((state) => state.pricing.reviewedListings.listingIds);

	return (
		<TicketProperties
			data={{
				id: rowData.Item_ID,
				eDelivery: rowData.SH_eDelivery,
				ticketAttached: rowData.Ticket_ID_Attached,
				sharedWithGT: rowData.IsListingSharedWithGT,
				pdfsAttached: rowData.totalPDFsAttached,
				barcodesAttached: rowData.BarcodesAttachedNumber,
				scheduledPricing: rowData.Is_Scheduled_Pricing,
				flashSale: rowData.FlashSaleEnabled,
				onHold: rowData.On_Hold,
				onHoldComments: rowData.On_Hold_Comments,
				reviewed: reviewedListingIds.includes(rowData.Listing_ID),
			}}
		/>
	);
};

export default Properties;
